// client/src/context/AuthContext.jsx - 修复jwt导入和使用
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import axios from 'axios';
import jwt_decode from 'jwt-decode'; // 正确导入
import { toast } from 'react-hot-toast';

// 创建认证上下文
const AuthContext = createContext(null);

// 使用认证上下文的自定义Hook
export const useAuth = () => useContext(AuthContext);

// 认证提供者组件
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  // 切换账号时清空 React Query 内存缓存, 防止新账号页面瞬显上一个账号的数据
  const queryClient = useQueryClient();

  // 设置axios默认请求头
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // 检查令牌是否有效
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // 检查令牌是否过期
        const decodedToken = jwt_decode(token); // 使用正确的导入名称
        const currentTime = Date.now() / 1000;

        if (decodedToken.exp < currentTime) {
          // 令牌已过期
          logout();
          return;
        }

        // 验证令牌有效性
        const response = await axios.get('/api/auth/profile');
        setCurrentUser(response.data.admin);
      } catch (error) {
        console.error('令牌验证错误:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]); // 依赖项应该加入logout

  // 登录(返回 admin 对象, 供调用方按菜单权限决定落地页; 失败返回 false)
  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', { username, password });
      const { token: newToken, admin } = response.data;

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setCurrentUser(admin);
      // 清掉上一个账号的查询缓存: 否则新账号首屏会命中旧缓存瞬显他人数据
      queryClient.clear();
      toast.success('登录成功');
      
      return admin;
    } catch (error) {
      console.error('登录错误:', error);
      const message = error.response?.data?.message || '登录失败';
      toast.error(message);
      
      return false;
    }
  };

  // 注册(自动登录, 返回 admin 对象; 失败返回 false)
  const register = async (username, password, email) => {
    try {
      const response = await axios.post('/api/auth/register', { username, password, email });
      const { token: newToken, admin } = response.data;

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setCurrentUser(admin);
      // 注册即登录, 同样清缓存(防同浏览器刚登出其他账号的场景)
      queryClient.clear();
      toast.success('注册成功');
      
      return admin;
    } catch (error) {
      console.error('注册错误:', error);
      const message = error.response?.data?.message || '注册失败';
      toast.error(message);
      
      return false;
    }
  };

  // 注销
  const logout = React.useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setCurrentUser(null);
    // 登出即清全部查询缓存, 避免下一把登录(哪怕是同浏览器换账号)看到残留数据
    queryClient.clear();
    navigate('/login');
  }, [navigate, queryClient]);

  // 全局401拦截: 令牌失效/账号过期自动登出并提示
  // (登录接口自身的401是密码错误, 不拦截, 交给登录页自己提示)
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const isLoginRequest = (error.config?.url || '').includes('/api/auth/login');
        if (status === 401 && !isLoginRequest) {
          if (error.response?.data?.code === 'ACCOUNT_EXPIRED') {
            toast.error('账号已过期，请联系管理员延期');
          }
          logout();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [logout]);

  // 修改密码
 const changePassword = async (currentPassword, newPassword) => {
    try {
        const response = await axios.put('/api/auth/password', { currentPassword, newPassword });
        
        // 处理成功的响应
        if (response.data.success) {
            toast.success('密码修改成功');
            return true;
        } else {
            // 如果响应的 format 并没有成功
            throw new Error('密码修改未成功，请重试');
        }
        
    } catch (error) {
        console.error('修改密码错误:', error);
        
        // 尝试获取服务器返回的错误信息
        const message = error.response?.data?.errors?.[0]?.message || '修改密码失败';
        toast.error(message);
        
        return false;
    }
};

  // VIP 判定(贴牌权益: 自定义标题/自定义背景图):
  //   超管恒true; 普通用户以 is_vip 为准, 并对"有权限但列未回填"的老数据兜底
  const isVip = currentUser?.role === 'admin'
    || !!currentUser?.isVip
    || (currentUser?.role === 'user'
        && Array.isArray(currentUser?.menuPermissions)
        && currentUser.menuPermissions.length > 0);

  // 刷新当前用户信息(贴牌保存后即时联动Sidebar/Layout, 无需重登)
  const refreshUser = async () => {
    try {
      const response = await axios.get('/api/auth/profile');
      setCurrentUser(response.data.admin);
      return true;
    } catch (error) {
      console.error('刷新用户信息失败:', error);
      return false;
    }
  };

  // 认证上下文值
  const value = {
    currentUser,
    loading,
    login,
    register,
    logout,
    changePassword,
    refreshUser,
    isAuthenticated: !!currentUser,
    isVip
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};